// src/lib/map/footprintStyle.js
//
// THE ONLY HOME OF FOOTPRINT COLORS AND OPACITIES (footprint restyle S-b, T1).
// A map's entitled imagery is drawn as an inverse mask: the world minus what
// the org may see, darkened in navy, with a two-tone edge (a dark casing under
// a white line) on each entitled footprint. GET /imagery/entitled (authz-api,
// S-a) answers {mask, features}; this module turns that into layers. No other
// file under src/ may carry a footprint color literal: footprintStyle.scan.test.js
// fails on one.
//
// Roles: each map that shows the mask says which it is, and the role (with the
// basemap's tone) decides the mask's opacity. dashboard and paper draw no mask.
//
//   role        satellite/dark   light basemap
//   explore          0.45            0.35
//   editor           0.45            0.35
//   newPlan          0.45            0.35
//   planPage         0.25            0.25
//   plansList        0.25            0.25
//   dashboard         off             off
//   paper             off             off
//
// Selection (S-c1, DB 2026-11-03): the selected capture(s), by dataset_key.
// Below a dataset's minzoom (its tiles not drawn yet): an orange diagonal
// hatch, a dark casing and a static dashed orange T3 edge (~3 px); the hatch
// fades out over z(minzoom-1) to z(minzoom). At and above the minzoom: a solid
// orange T4 edge (~1 px). explore, editor, newPlan and planPage draw it; the
// plans list, the dashboard and Paper view do not. No blue anywhere, no
// animation. fill-pattern and line-dasharray take no feature-state, so the
// selected layers filter on dataset_key (setSelectedKeys) and read each
// feature's own minzoom through zoom stops 0..24.
//
// A missing or removed map (REM-703): every function below returns without a
// call into it, and ensureFootprintStyle says nothing is drawn.

import { isLiveMap } from "./liveMap";

/** The selection data color (DB, 2026-11-02). */
export const ORANGE = "#FF7F00";
/** The mask's tint. A constant here, not a theme token (DB, 2026-11-02). */
export const NAVY = "#00324D";
/** The dark half of the edge, under the white line. */
export const CASING = "#0b1220";
export const WHITE = "#ffffff";

export const FP_SRC = Object.freeze({ mask: "fp-mask", features: "fp-features" });
export const FP_LAYER = Object.freeze({
  mask: "fp-mask-fill",
  casing: "fp-edge-casing",
  edge: "fp-edge",
  selHatch: "fp-sel-hatch",
  selCasing: "fp-sel-casing",
  selT3: "fp-sel-edge-t3",
  selT4: "fp-sel-edge-t4",
});
/** The selected capture's layers, bottom to top. */
export const FP_SEL_LAYER_IDS = Object.freeze([FP_LAYER.selHatch, FP_LAYER.selCasing, FP_LAYER.selT3, FP_LAYER.selT4]);
/** Bottom to top. */
export const FP_LAYER_IDS = Object.freeze([FP_LAYER.mask, FP_LAYER.casing, FP_LAYER.edge, ...FP_SEL_LAYER_IDS]);
/** The hatch's image id (a map image, re-added after a style reload). */
export const FP_HATCH_IMAGE = "fp-sel-hatch-pattern";

/** S-a's world ring: what the route answers with nothing entitled. */
export const WORLD_RING = Object.freeze([[-180, -85.0511], [180, -85.0511], [180, 85.0511], [-180, 85.0511], [-180, -85.0511]]);
/** Fail closed: before an answer, and after any error, the whole world is masked. */
export const WORLD_MASKED = Object.freeze({ mask: { type: "Polygon", coordinates: [WORLD_RING] }, features: [] });

export const ROLES = Object.freeze(["explore", "editor", "newPlan", "planPage", "plansList", "dashboard", "paper"]);
const FULL = { dark: 0.45, light: 0.35 };
const QUIET = { dark: 0.25, light: 0.25 };
const MASK_OPACITY = Object.freeze({
  explore: FULL, editor: FULL, newPlan: FULL, planPage: QUIET, plansList: QUIET, dashboard: null, paper: null,
});

// Light-toned Mapbox styles: the mask is lighter over them (0.35).
const LIGHT_BASEMAPS = new Set(["light-v10", "light-v11", "streets-v11", "streets-v12", "outdoors-v11", "outdoors-v12"]);
/** "light" for a light-toned basemap, else "dark" (satellite, satellite streets, dark). */
export function basemapTone(basemapId) {
  return LIGHT_BASEMAPS.has(basemapId) ? "light" : "dark";
}

/** The mask's fill opacity, or null where the role draws no mask. */
export function maskOpacity(role, tone = "dark") {
  if (!(role in MASK_OPACITY)) throw new Error(`footprintStyle: unknown role ${role}`);
  const o = MASK_OPACITY[role];
  return o ? o[tone === "light" ? "light" : "dark"] : null;
}

// Edge widths at z8 / z12 / z16: casing 2 → 3.5 → 5, white 1 → 1.5 → 2.
const byZoom = (a, b, c) => ["interpolate", ["linear"], ["zoom"], 8, a, 12, b, 16, c];
export const CASING_WIDTH = Object.freeze(byZoom(2, 3.5, 5));
export const EDGE_WIDTH = Object.freeze(byZoom(1, 1.5, 2));

/** Roles that draw the selected capture (DB, S-c decision 6). */
const SELECTED_ROLES = new Set(["explore", "editor", "newPlan", "planPage"]);
/** The role draws the selected capture: its T3/T4 and hatch (S-c1), and its chip (S-c2). */
export const drawsSelection = (role) => SELECTED_ROLES.has(role);
const MAX_Z = 24;
const STOPS = Array.from({ length: MAX_Z + 1 }, (_, k) => k);
const MINZOOM = ["coalesce", ["get", "minzoom"], 0];
// 1 over z <= minzoom-1, 0 from z >= minzoom, linear between: each stop k reads clamp(minzoom - k, 0, 1).
const HATCH_FADE = ["interpolate", ["linear"], ["zoom"], ...STOPS.flatMap((k) => [k, ["max", 0, ["min", 1, ["-", MINZOOM, k]]]])];
// below: 1 while floor(zoom) < minzoom (minzoom is an integer), else 0; at: the inverse.
const belowMinzoom = (below, at) => ["step", ["zoom"], ["case", [">", MINZOOM, 0], below, at],
  ...STOPS.slice(1).flatMap((k) => [k, ["case", [">", MINZOOM, k], below, at]])];
export const T3_WIDTH = 3;
export const T4_WIDTH = 1;
export const SEL_CASING_WIDTH = 5;
const T3_DASH = [2, 1.5];
const noneSelected = () => ["in", ["get", "dataset_key"], ["literal", []]];
const selectedFilter = (keys) => ["in", ["get", "dataset_key"], ["literal", [...keys]]];

function selectedLayers() {
  const line = (id, paint) => ({ id, type: "line", source: FP_SRC.features, filter: noneSelected(),
    layout: { "line-join": "round", "line-cap": "butt" }, paint });
  return [
    { id: FP_LAYER.selHatch, type: "fill", source: FP_SRC.features, filter: noneSelected(),
      paint: { "fill-pattern": FP_HATCH_IMAGE, "fill-opacity": HATCH_FADE, "fill-antialias": false } },
    line(FP_LAYER.selCasing, { "line-color": CASING, "line-width": SEL_CASING_WIDTH, "line-opacity": belowMinzoom(1, 0) }),
    line(FP_LAYER.selT3, { "line-color": ORANGE, "line-width": T3_WIDTH, "line-dasharray": T3_DASH, "line-opacity": belowMinzoom(1, 0) }),
    line(FP_LAYER.selT4, { "line-color": ORANGE, "line-width": T4_WIDTH, "line-opacity": belowMinzoom(0, 1) }),
  ];
}

const HATCH_SIZE = 16;
const HATCH_PERIOD = 8;
const HATCH_STROKE = 2;
/** The hatch: orange diagonal strokes on transparent, a tile that repeats seamlessly. */
export function hatchImage() {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(ORANGE.slice(i, i + 2), 16));
  const data = new Uint8Array(HATCH_SIZE * HATCH_SIZE * 4);
  for (let y = 0; y < HATCH_SIZE; y++) for (let x = 0; x < HATCH_SIZE; x++) {
    if ((x + y) % HATCH_PERIOD >= HATCH_STROKE) continue;
    data.set([r, g, b, 255], (y * HATCH_SIZE + x) * 4);
  }
  return { width: HATCH_SIZE, height: HATCH_SIZE, data };
}

/** Add the hatch image unless the map has it (a style reload drops map images). */
export function ensureHatchImage(map) {
  if (!isLiveMap(map) || map.hasImage(FP_HATCH_IMAGE)) return;
  map.addImage(FP_HATCH_IMAGE, hatchImage());
}

/**
 * The layers, bottom to top, for a role and tone; [] where the role draws no
 * mask. Each layer is new on each call (MapLibre keeps what it is given).
 */
export function footprintLayers({ role, tone = "dark" }) {
  const opacity = maskOpacity(role, tone);
  if (opacity == null) return [];
  return [
    { id: FP_LAYER.mask, type: "fill", source: FP_SRC.mask,
      paint: { "fill-color": NAVY, "fill-opacity": opacity, "fill-antialias": false } },
    { id: FP_LAYER.casing, type: "line", source: FP_SRC.features,
      layout: { "line-join": "round" }, paint: { "line-color": CASING, "line-width": byZoom(2, 3.5, 5) } },
    { id: FP_LAYER.edge, type: "line", source: FP_SRC.features,
      layout: { "line-join": "round" }, paint: { "line-color": WHITE, "line-width": byZoom(1, 1.5, 2) } },
    ...(SELECTED_ROLES.has(role) ? selectedLayers() : []),
  ];
}

const maskFeature = (entitled) => ({ type: "Feature", properties: {}, geometry: (entitled ?? WORLD_MASKED).mask });
const featureCollection = (entitled) => ({ type: "FeatureCollection", features: (entitled ?? WORLD_MASKED).features ?? [] });

/**
 * Add the sources and layers under `anchor` (a layer id; null or missing: on
 * top), or, when they are there, set the role's opacity. With a role that
 * draws no mask, the layers are removed. Returns whether the mask is drawn.
 * `entitled` seeds a new source; omitted, the world is masked.
 */
export function ensureFootprintStyle(map, anchor, { role, tone = "dark", entitled = null, visible = true } = {}) {
  if (!isLiveMap(map)) return false;
  const layers = footprintLayers({ role, tone });
  if (layers.length === 0) {
    removeFootprintStyle(map);
    return false;
  }
  if (!map.getSource(FP_SRC.mask)) map.addSource(FP_SRC.mask, { type: "geojson", data: maskFeature(entitled) });
  if (!map.getSource(FP_SRC.features)) {
    // promoteId: feature-state needs an id MapLibre accepts, and dataset ids are UUIDs.
    map.addSource(FP_SRC.features, { type: "geojson", data: featureCollection(entitled), promoteId: "dataset_id" });
  }
  // A role without selected styling (the plans list) drops any left from a role with it.
  const ids = new Set(layers.map((l) => l.id));
  for (const id of [...FP_SEL_LAYER_IDS].reverse()) if (!ids.has(id) && map.getLayer(id)) map.removeLayer(id);
  if (ids.has(FP_LAYER.selHatch)) ensureHatchImage(map);
  const before = anchor && map.getLayer(anchor) ? anchor : undefined;
  for (const layer of layers) {
    if (map.getLayer(layer.id)) continue;
    map.addLayer({ ...layer, layout: { ...(layer.layout ?? {}), visibility: visible ? "visible" : "none" } }, before);
  }
  map.setPaintProperty(FP_LAYER.mask, "fill-opacity", maskOpacity(role, tone));
  setFootprintVisible(map, visible);
  return true;
}

/** Point both sources at an answer of GET /imagery/entitled (null: the world masked). */
export function setFootprintData(map, entitled) {
  if (!isLiveMap(map)) return;
  map.getSource(FP_SRC.mask)?.setData(maskFeature(entitled));
  map.getSource(FP_SRC.features)?.setData(featureCollection(entitled));
}

export function setFootprintVisible(map, on) {
  if (!isLiveMap(map)) return;
  for (const id of FP_LAYER_IDS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
  }
}

export function removeFootprintStyle(map) {
  if (!isLiveMap(map)) return;
  for (const id of [...FP_LAYER_IDS].reverse()) if (map.getLayer(id)) map.removeLayer(id);
  for (const id of Object.values(FP_SRC)) if (map.getSource(id)) map.removeSource(id);
}

/** Show the selected styling on the features whose dataset_key is in `keys` (an array; [] for none). */
export function setSelectedKeys(map, keys) {
  if (!isLiveMap(map)) return;
  const filter = selectedFilter(keys ?? []);
  for (const id of FP_SEL_LAYER_IDS) if (map.getLayer(id)) map.setFilter(id, filter);
}
